import React, { useState, useEffect } from 'react';
import { modelService, Model } from '../../services/modelService';
import { useModelBuilder } from '../../context/ModelBuilderContext';
import './ModelBrowser.css';

interface ModelBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
}

const ModelBrowser: React.FC<ModelBrowserProps> = ({ isOpen, onClose, projectId }) => {
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { loadArchitecture } = useModelBuilder();

  useEffect(() => {
    if (isOpen) {
      loadModels();
    }
  }, [isOpen, projectId]);

  const loadModels = async () => {
    setLoading(true);
    setError(null);
    try {
      const modelList = await modelService.listModels(projectId);
      setModels(modelList);
    } catch (err: any) {
      setError(err.message || 'Failed to load models');
      console.error('Error loading models:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadModel = async (model: Model) => {
    try {
      setLoading(true);
      const { architecture } = await modelService.getModel(model.id);
      
      if (architecture) {
        loadArchitecture(architecture);
        onClose();
      } else {
        alert('Model architecture not found');
      }
    } catch (err: any) {
      alert(`Failed to load model: ${err.message || 'Unknown error'}`);
      console.error('Error loading model:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteModel = async (model: Model, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!window.confirm(`Are you sure you want to delete "${model.display_name || model.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await modelService.deleteModel(model.id);
      // Reload models list
      await loadModels();
    } catch (err: any) {
      alert(`Failed to delete model: ${err.message || 'Unknown error'}`);
      console.error('Error deleting model:', err);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="model-browser-overlay" onClick={onClose}>
      <div className="model-browser-modal" onClick={(e) => e.stopPropagation()}>
        <div className="model-browser-header">
          <h2>📂 Saved Models</h2>
          <button className="model-browser-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="model-browser-content">
          {loading && models.length === 0 ? (
            <div className="model-browser-loading">
              <p>Loading models...</p>
            </div>
          ) : error ? (
            <div className="model-browser-error">
              <p>Error: {error}</p>
              <button onClick={loadModels}>Retry</button>
            </div>
          ) : models.length === 0 ? (
            <div className="model-browser-empty">
              <p>No saved models found.</p>
              <p className="model-browser-empty-hint">
                Create a model in the Model Builder and save it to see it here.
              </p>
            </div>
          ) : (
            <div className="model-browser-grid">
              {models.map((model) => (
                <div key={model.id} className="model-browser-card">
                  <div className="model-browser-card-header">
                    <h3>{model.display_name || model.name}</h3>
                    <button
                      className="model-browser-delete-btn"
                      onClick={(e) => handleDeleteModel(model, e)}
                      title="Delete model"
                    >
                      🗑️
                    </button>
                  </div>
                  {model.description && (
                    <p className="model-browser-card-description">{model.description}</p>
                  )}
                  <div className="model-browser-card-meta">
                    <span className="model-browser-card-date">
                      Created: {formatDate(model.created_at)}
                    </span>
                    {model.training_status && (
                      <span className={`model-browser-card-status status-${model.training_status}`}>
                        {model.training_status}
                      </span>
                    )}
                  </div>
                  <button
                    className="model-browser-load-btn"
                    onClick={() => handleLoadModel(model)}
                    disabled={loading}
                  >
                    📥 Load Model
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModelBrowser;

